/**
 * Liste de discussions — composant PRÉSENTATIONNEL (les données sont fournies
 * par l'appelant, `useDiscussions()` côté route) : réutilisé tel quel pour la
 * page « Mes discussions » et pour la section filtrée d'une fiche contact.
 */

import { MessageCircle } from 'lucide-react';
import { Paginator, cn } from '@jeraisonne/ui';
import { usePagedList } from '@/lib/use-paged-list';
import { AvatarCircle } from '@/components/avatar-circle';
import { discussionOtherParty, type DiscussionRow } from '@/lib/discussions/types';
import {
  hasUnreadDiscussion,
  type DiscussionViewerRole,
} from '@/lib/discussions/discussion-unread';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `il y a ${hrs} h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `il y a ${days} j`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export function DiscussionsList({
  discussions,
  myRole,
  emptyLabel = 'Aucune discussion pour l’instant.',
  onOpen,
}: {
  discussions: readonly DiscussionRow[];
  myRole: DiscussionViewerRole;
  emptyLabel?: string;
  onOpen: (discussionId: string) => void;
}) {
  const { items: page, paginator } = usePagedList(discussions, {
    unit: 'discussions',
    label: 'Pages des discussions',
  });
  if (discussions.length === 0) {
    return (
      <div className="border-ink-200 rounded-2xl border bg-white p-6 text-center">
        <p className="text-ink-500 font-sans text-sm">{emptyLabel}</p>
      </div>
    );
  }

  return (
    <>
      <ul className="space-y-2">
        {page.map((d) => {
          const { name: otherName, avatarUrl: otherAvatar } = discussionOtherParty(d, myRole);
          const unread = hasUnreadDiscussion(d, myRole);
          return (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => onOpen(d.id)}
                className="border-ink-100 flex min-h-[2.75rem] w-full items-center gap-3 rounded-2xl border bg-white p-3 text-left transition-colors active:scale-[0.98]"
              >
                <AvatarCircle name={otherName} src={otherAvatar} size="md" />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        'truncate text-sm',
                        unread ? 'text-ink-900 font-bold' : 'text-ink-900 font-semibold',
                      )}
                    >
                      {d.title}
                    </span>
                    {unread && (
                      <span
                        aria-label="Non lu"
                        className="bg-primary-500 inline-block size-2 shrink-0 rounded-full"
                      />
                    )}
                  </span>
                  <span className="text-ink-500 mt-0.5 flex items-center gap-1 truncate text-xs">
                    <MessageCircle className="size-3 shrink-0" aria-hidden />
                    {otherName} · {relativeTime(d.lastMessageAt)}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <Paginator {...paginator} />
    </>
  );
}
